import { initializeApp } from "firebase/app";
import {
  connectFirestoreEmulator,
  enableNetwork,
  getFirestore,
} from "firebase/firestore";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  signInAnonymously,
} from "firebase/auth";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { createLogger } from "../utils/logger";

const logger = createLogger("firebase.config");

// Enable App Check debug tokens in development BEFORE initializing Firebase
if (import.meta.env.DEV && import.meta.env.VITE_E2E_TESTING !== "true") {
  // Set the debug token flag globally before any Firebase initialization
  // Use a safe check for browser environment
  const globalThisRef: Record<string, unknown> = (() => {
    if (typeof window !== "undefined") return window;
    if (typeof self !== "undefined") return self;
    if (typeof global !== "undefined") return global;
    return {};
  })();

  globalThisRef.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  logger.info("App Check Debug Mode: Debug token will be logged to console");
  logger.info(
    "Copy the debug token from the console and add it to Firebase Console",
  );
  logger.info(
    "Firebase Console: https://console.firebase.google.com/project/omnom-812a4/appcheck/apps",
  );
}

// Validate required environment variables with more specific error messages
const requiredEnvVars = {
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env
    .VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
  VITE_RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
} as const;

// Check for missing environment variables
const missingVars = Object.entries(requiredEnvVars)
  .filter(([, value]) => !value)
  .map(([key]) => key);

// Skip strict env var check during E2E testing (we use emulator + dummy creds)
if (missingVars.length > 0 && import.meta.env.VITE_E2E_TESTING !== "true") {
  const errorMessage = `Missing required environment variables: ${missingVars.join(", ")}. Please check your .env.local file.`;
  logger.error("Firebase Configuration Error:", errorMessage);
  throw new Error(errorMessage);
}

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  ...(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    ? { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }
    : {}),
} as const;

// Log configuration status (without exposing sensitive data)
logger.info("Firebase Configuration Status:", {
  environment: import.meta.env.MODE,
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  hasApiKey: !!firebaseConfig.apiKey,
  hasAppId: !!firebaseConfig.appId,
  hasMeasurementId: !!import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check with lazy loading and environment-based configuration
let appCheckInstance: unknown = null;
let appCheckInitializationError: Error | null = null;

interface FirebaseInitializationState {
  isInitialized: boolean;
  isLoading: false;
  error: string | null;
  appCheckEnabled: boolean;
}

// Enhanced App Check initialization with proper error handling
const initializeAppCheckIfNeeded = () => {
  if (import.meta.env.VITE_E2E_TESTING === "true") {
    logger.info(
      "E2E testing mode: Firebase App Check with reCAPTCHA Enterprise is DISABLED.",
    );
    appCheckInstance = {
      // Provide a minimal mock interface if needed by other parts of your app
      // For example, if other code expects a getToken function:
      // getToken: () => Promise.resolve({ token: "mock-e2e-app-check-token" }),
      // onTokenChanged: () => { return () => {}; } // Mock unsubscribe function
    }; // Assign a basic mock or a more specific mock if your app interacts with appCheck properties/methods
  } else {
    logger.info("Initializing Firebase App Check with reCAPTCHA Enterprise.");

    if (!import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
      throw new Error(
        "VITE_RECAPTCHA_SITE_KEY is required for App Check initialization",
      );
    }

    // Handle placeholder key gracefully
    if (
      import.meta.env.VITE_RECAPTCHA_SITE_KEY === "your-recaptcha-site-key-here"
    ) {
      if (import.meta.env.DEV) {
        logger.warn("️ Using placeholder reCAPTCHA key in development mode");
        logger.warn(
          "App Check will use debug tokens - check console for debug token",
        );
      } else {
        throw new Error(
          "Production deployment requires valid VITE_RECAPTCHA_SITE_KEY",
        );
      }
    }

    try {
      appCheckInstance = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(
          import.meta.env.VITE_RECAPTCHA_SITE_KEY,
        ),

        // Optional argument. If true, the SDK automatically refreshes App Check
        // tokens as needed.
        isTokenAutoRefreshEnabled: true,
      });

      // Log additional info about debug mode
      if (import.meta.env.DEV) {
        logger.info(
          "App Check initialized in development mode - check for debug token above",
        );
      }
    } catch (error: unknown) {
      logger.error("App Check initialization failed:", error);

      // In development, continue without App Check for better DX
      if (import.meta.env.DEV) {
        logger.warn("Continuing in development mode without App Check");
        appCheckInstance = null;
      } else {
        // In production, this is a critical failure
        throw error;
      }
    }
  }

  return appCheckInstance;
};

// App Check must be initialized before any Firebase service is created. Keep
// this ordering synchronous so a cold route cannot race ahead of protection.
try {
  appCheckInstance = initializeAppCheckIfNeeded();
} catch (error: unknown) {
  appCheckInitializationError =
    error instanceof Error ? error : new Error(String(error));
}

// Firebase services are created only after the App Check initialization attempt.
const db = getFirestore(app);

// Initialize Auth
const auth = initializeAuth(app, {
  persistence: browserLocalPersistence,
});

// Initialize Cloud Functions in the same region as deployed callables
const functions = getFunctions(app, "europe-west1");

// Connect to Firebase emulator in test mode
const initializeE2EEmulators = async () => {
  if (import.meta.env.VITE_E2E_TESTING === "true") {
    logger.info("E2E Testing Mode: Connecting to Firebase emulators...");
    try {
      connectFirestoreEmulator(db, "localhost", 8080);
      connectAuthEmulator(auth, "http://localhost:9099");
      connectFunctionsEmulator(functions, "localhost", 5002);
      logger.info("Connected to Firebase emulators");
      // Sign in anonymously to ensure authenticated context for security rules
      try {
        await signInAnonymously(auth);
        logger.info("Anonymous auth for E2E testing successful");
      } catch (authErr: unknown) {
        logger.error("Anonymous auth failed during E2E setup:", authErr);
      }
    } catch (error: unknown) {
      logger.warn(
        "️ Emulator connection failed (may already be connected):",
        error,
      );
    }
  } else {
    // Force network-only mode for reliability with shared lists
    logger.info("Configuring Firestore for direct network access...");

    // Ensure network is enabled to prevent working in offline mode
    enableNetwork(db).catch((err) => {
      logger.error("Failed to enable network for Firestore:", err);
    });
  }
};

// Initialize E2E emulators if needed
initializeE2EEmulators().catch((error) =>
  logger.error("Failed to initialize emulators", error),
);

// Optimized retry operation with exponential backoff and better error handling
const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
): Promise<T> => {
  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Set a timeout for each individual operation
      const operationPromise = operation();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(new Error(`Operation timed out (attempt ${attempt + 1})`)),
          5000,
        ),
      );

      return await Promise.race([operationPromise, timeoutPromise]);
    } catch (error: unknown) {
      lastError = error as Error;
      const err = error as { code?: string; message?: string; name?: string };
      logger.warn(
        `Operation failed (attempt ${attempt + 1}/${maxRetries}):`,
        err.message,
      );

      // Add detailed logging for debugging
      logger.error("Firestore operation error details:", {
        code: err.code,
        message: err.message,
        name: err.name,
      });

      // Don't wait after the last attempt
      if (attempt < maxRetries - 1) {
        // Use shorter delays for faster fallback with exponential backoff
        const delay = Math.min(Math.pow(2, attempt) * 500, 2000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError!;
};

// Sign in anonymously with proper error reporting and timeout
const signInForTesting = async (): Promise<any> => {
  try {
    logger.info("Starting anonymous sign-in process...");
    // Use a shorter timeout for the auth operation
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Auth timeout after 5 seconds")), 5000),
    );

    const authPromise = signInAnonymously(auth);
    const result = await Promise.race([authPromise, timeout]);

    logger.info("Anonymous sign-in successful");
    return result;
  } catch (error: unknown) {
    const err = error as {
      code?: string;
      message?: string;
      name?: string;
      stack?: string;
    };
    logger.error("Error during anonymous sign-in:", {
      code: err.code,
      message: err.message,
      name: err.name,
      stack: err.stack,
    });
    throw error;
  }
};

// Preserve the existing readiness interface for callers while returning the
// single module-owned initialization result.
const getAppCheck = async (): Promise<unknown> => {
  if (appCheckInitializationError) {
    throw appCheckInitializationError;
  }

  return appCheckInstance;
};

const getFirebaseInitializationState = (): FirebaseInitializationState => ({
  isInitialized: appCheckInitializationError === null,
  isLoading: false,
  error: appCheckInitializationError?.message ?? null,
  appCheckEnabled: appCheckInstance !== null,
});

export {
  app,
  db,
  auth,
  functions,
  retryOperation,
  signInForTesting,
  getAppCheck,
  getFirebaseInitializationState,
};
